export const LIST_TABLE_PAGE_SIZE_VALUES = [10, 25, 50, 0] as const;
export type ListTablePageSizeChoice = (typeof LIST_TABLE_PAGE_SIZE_VALUES)[number];

export function isListTablePageSizeChoice(value: number): value is ListTablePageSizeChoice {
  return (LIST_TABLE_PAGE_SIZE_VALUES as readonly number[]).includes(value);
}

export type ListTablePaginationDisplay = {
  triggerLabel: string;
  selectedChoice: ListTablePageSizeChoice;
};

export function getListTablePaginationDisplay(params: {
  pageSize: number;
  pageSizeTriggerLabel?: string;
  selectedPageSizeChoice?: number;
}): ListTablePaginationDisplay {
  const { pageSize, pageSizeTriggerLabel, selectedPageSizeChoice } = params;

  if (selectedPageSizeChoice !== undefined && isListTablePageSizeChoice(selectedPageSizeChoice)) {
    const trigger =
      selectedPageSizeChoice === 0
        ? "All"
        : pageSizeTriggerLabel && pageSizeTriggerLabel !== "All"
          ? pageSizeTriggerLabel
          : String(selectedPageSizeChoice);
    return { triggerLabel: trigger, selectedChoice: selectedPageSizeChoice };
  }

  if (pageSize === 0) {
    return { triggerLabel: "All", selectedChoice: 0 };
  }

  if (isListTablePageSizeChoice(pageSize) && pageSize !== 0) {
    return { triggerLabel: String(pageSize), selectedChoice: pageSize };
  }

  return { triggerLabel: "All", selectedChoice: 0 };
}

export type ServerListPagination = {
  total_cases?: number;
  total_pages?: number;
  total_clients?: number;
};

export function resolveServerListTotals(pagination: ServerListPagination | null | undefined): {
  totalItems: number;
  totalPages: number;
} {
  const totalItems = pagination?.total_cases ?? pagination?.total_clients ?? 0;
  const totalPages = pagination?.total_pages ?? (totalItems > 0 ? 1 : 1);
  return { totalItems, totalPages: Math.max(1, totalPages) };
}

export function resolveLocalListPagination<T>(
  items: readonly T[],
  page: number,
  rowsPerPage: number
): { totalItems: number; totalPages: number; page: number; paginatedItems: T[] } {
  const totalItems = items.length;
  const safeRowsPerPage = Math.max(1, rowsPerPage || 1);
  const totalPages = Math.max(1, Math.ceil(totalItems / safeRowsPerPage) || 1);
  const safePage = Math.min(Math.max(0, page), totalPages - 1);
  const start = safePage * safeRowsPerPage;
  return {
    totalItems,
    totalPages,
    page: safePage,
    paginatedItems: items.slice(start, start + safeRowsPerPage),
  };
}
